import * as Sentry from "@sentry/react";
import { Button, Icon, cn } from "@nexus/ui";
import { useState, type MouseEvent as ReactMouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { isCustomAssetScript } from "@/features/marketplace/lib/custom-asset";
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import { ScriptSentToVendorCard } from "@/features/opportunities/components/scripts/ScriptSentToVendorCard/ScriptSentToVendorCard.js";
import type { PersonaScriptDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";
import {
  downloadPersonaScriptDocx,
  scriptDocxLabels,
} from "@/features/persona-opportunities/lib/persona-script-docx";

import { usePersonaScriptClipboard } from "./usePersonaScriptClipboard.js";

export interface PersonaScriptActionsFooterProps {
  script: PersonaScriptDto;
  showAddThreeHooks: boolean;
  isSuggesting: boolean;
  onAddThreeHooks: () => void;
  onAiEdit: () => void;
  onSendToVendor: () => void;
  isAiEditing: boolean;
  sentMeta?: SentScriptMeta | null;
  orderHref?: string;
  onOpenOrder?: (
    orderId: string,
    orderScriptId: string | undefined,
    event: ReactMouseEvent<HTMLAnchorElement>,
  ) => void;
  /**
   * Hub drawer renders copy, share, AI edit, add-3-hooks, send, and the busy status
   * in the sheet header, so the footer renders nothing.
   */
  actionsPlacement?: "footer" | "header";
}

const LOADING_BUTTON_CLASS =
  "ring-1 ring-[var(--ai)] disabled:opacity-80 [&_svg]:animate-spin";
/** Figma: 36×36 outlined squares, 16px glyphs — do not use size="icon" (28px). */
const ICON_BUTTON_CLASS =
  "h-9 w-9 min-h-9 min-w-9 p-0 has-[>svg]:p-0 [&_svg]:size-4";
/** Figma: teal 1px border + faint mint fill, same 36px height as the row. */
const AI_BUTTON_CLASS =
  "h-9 border border-[var(--ai-border)] bg-[var(--ai-fill-light)] text-[var(--ai-text)] hover:bg-[var(--ai-fill-medium)]";
const ROW_BUTTON_CLASS = "h-9";

function PaperPlaneIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-4 shrink-0"
      data-icon="send"
    >
      <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
      <path d="m21.854 2.147-10.94 10.939" />
    </svg>
  );
}

function LoadingStatus({ children }: { children: string }) {
  return (
    <p
      role="status"
      className="flex items-center gap-2 rounded-md bg-[var(--ai-fill-light)] px-3 py-2 text-xs text-[var(--ai-text)]"
    >
      <Icon name="loader" color="inherit" size="sm" className="animate-spin" />
      {children}
    </p>
  );
}

/** In-flight AI work on one script. The hub drawer pins it under its header toolbar. */
export function PersonaScriptBusyStatus({
  isSuggesting,
  isAiEditing,
  className,
}: {
  isSuggesting: boolean;
  isAiEditing: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!isSuggesting && !isAiEditing) return null;
  return (
    <div className={cn("flex flex-col gap-1", className)} aria-live="polite">
      {isSuggesting ? (
        <LoadingStatus>
          {t(
            "personaOpportunities.scripts.hookVariations.writing",
            "Writing 3 alternative hooks...",
          )}
        </LoadingStatus>
      ) : null}
      {isAiEditing ? (
        <LoadingStatus>
          {t("personaOpportunities.scripts.actions.aiEditing", "AI editing this script...")}
        </LoadingStatus>
      ) : null}
    </div>
  );
}

export function PersonaScriptShareButton({
  linkCopied,
  isSharing = false,
  onShare,
}: {
  linkCopied: boolean;
  isSharing?: boolean;
  onShare: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="secondary"
      className={cn(ICON_BUTTON_CLASS, isSharing && "disabled:opacity-100")}
      iconStart={isSharing ? undefined : linkCopied ? "check" : "share"}
      onClick={onShare}
      disabled={isSharing}
      aria-busy={isSharing}
      aria-label={
        linkCopied
          ? t("personaOpportunities.scripts.actions.shareCopied", "Link copied!")
          : t("personaOpportunities.scripts.actions.share", "Share")
      }
    >
      {isSharing ? (
        <Icon
          name="loader"
          color="inherit"
          size="sm"
          className="animate-spin motion-reduce:animate-none"
        />
      ) : null}
    </Button>
  );
}

export function PersonaScriptDownloadButton({ script }: { script: PersonaScriptDto }) {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  if (isCustomAssetScript(script)) return null;

  const handleDownload = async () => {
    const untitled = t("personaOpportunities.scripts.untitledHook", {
      defaultValue: "Script {{slot}}",
      slot: script.slotIndex,
    });
    setIsDownloading(true);
    try {
      await downloadPersonaScriptDocx(script, untitled, scriptDocxLabels(t));
    } catch (error) {
      Sentry.captureException(error, { tags: { flow: "persona-script-download" } });
      toast.error(
        t(
          "personaOpportunities.scripts.actions.downloadFailed",
          "Could not download the script",
        ),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="secondary"
      className={cn(ICON_BUTTON_CLASS, isDownloading && "disabled:opacity-100")}
      iconStart={isDownloading ? undefined : "download"}
      onClick={() => void handleDownload()}
      disabled={isDownloading}
      aria-busy={isDownloading}
      aria-label={t("personaOpportunities.scripts.actions.download", "Download Script")}
    >
      {isDownloading ? (
        <Icon
          name="loader"
          color="inherit"
          size="sm"
          className="animate-spin motion-reduce:animate-none"
        />
      ) : null}
    </Button>
  );
}

export function PersonaScriptCopyButton({
  copied,
  onCopy,
  label,
}: {
  copied: boolean;
  onCopy: () => void;
  /** Idle label; briefs pass "Copy brief". */
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="secondary"
      className={ICON_BUTTON_CLASS}
      iconStart={copied ? "check" : "copy"}
      onClick={onCopy}
      aria-label={
        copied
          ? t("personaOpportunities.scripts.actions.copied", "Copied!")
          : (label ?? t("personaOpportunities.scripts.actions.copy", "Copy Script"))
      }
    />
  );
}

export function PersonaScriptAiEditButton({
  isAiEditing,
  onAiEdit,
  tone = "ai",
}: {
  isAiEditing: boolean;
  onAiEdit: () => void;
  /** Drawer header uses the same neutral outline as the other header controls. */
  tone?: "ai" | "neutral";
}) {
  const { t } = useTranslation();
  const isNeutral = tone === "neutral";
  return (
    <Button
      type="button"
      variant={isNeutral ? "secondary" : "ai"}
      iconStart={isAiEditing ? "loader" : "sparkles"}
      disabled={isAiEditing}
      aria-busy={isAiEditing}
      onClick={onAiEdit}
      className={
        isNeutral
          ? isAiEditing
            ? "[&_svg]:animate-spin"
            : undefined
          : isAiEditing
            ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}`
            : AI_BUTTON_CLASS
      }
    >
      {t("personaOpportunities.scripts.actions.aiEdit", "AI Edit")}
    </Button>
  );
}

export function PersonaScriptAddThreeHooksButton({
  isSuggesting,
  onAddThreeHooks,
}: {
  isSuggesting: boolean;
  onAddThreeHooks: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ai"
      iconStart={isSuggesting ? "loader" : "plus"}
      disabled={isSuggesting}
      aria-busy={isSuggesting}
      onClick={onAddThreeHooks}
      className={isSuggesting ? `${AI_BUTTON_CLASS} ${LOADING_BUTTON_CLASS}` : AI_BUTTON_CLASS}
    >
      {t("personaOpportunities.scripts.hookVariations.addThree", "Add 3 hooks")}
    </Button>
  );
}

export function PersonaScriptVendorAction({
  sentMeta,
  onSendToVendor,
  orderHref,
  onOpenOrder,
  sentCardClassName,
  sentPresentation = "card",
}: {
  sentMeta?: SentScriptMeta | null;
  onSendToVendor: () => void;
  orderHref?: string;
  onOpenOrder?: (
    orderId: string,
    orderScriptId: string | undefined,
    event: ReactMouseEvent<HTMLAnchorElement>,
  ) => void;
  sentCardClassName?: string;
  /**
   * Hub header keeps a button in the send slot. The set workspace footer keeps the banner.
   */
  sentPresentation?: "card" | "button";
}) {
  const { t } = useTranslation();
  if (!sentMeta) {
    return (
      <Button type="button" className={ROW_BUTTON_CLASS} onClick={onSendToVendor}>
        <PaperPlaneIcon />
        {t("personaOpportunities.scripts.actions.sendToVendor", "Send to production partner")}
      </Button>
    );
  }
  if (sentPresentation === "button" && orderHref) {
    return (
      <Button asChild className={ROW_BUTTON_CLASS}>
        <a
          href={orderHref}
          onClick={
            onOpenOrder
              ? (event) => onOpenOrder(sentMeta.orderId, sentMeta.orderScriptId, event)
              : undefined
          }
        >
          {t("opportunities.scripts.sentCard.viewOrder", "View order")}
          <Icon name="chevron-right" color="inherit" size="sm" aria-hidden />
        </a>
      </Button>
    );
  }
  return (
    <ScriptSentToVendorCard
      sentMeta={sentMeta}
      orderHref={orderHref}
      className={sentCardClassName}
      onOpenOrder={
        onOpenOrder
          ? (event) => onOpenOrder(sentMeta.orderId, sentMeta.orderScriptId, event)
          : undefined
      }
    />
  );
}

export function PersonaScriptActionsFooter({
  script,
  showAddThreeHooks,
  isSuggesting,
  onAddThreeHooks,
  onAiEdit,
  onSendToVendor,
  isAiEditing,
  sentMeta,
  orderHref,
  onOpenOrder,
  actionsPlacement = "footer",
}: PersonaScriptActionsFooterProps) {
  const { linkCopied, copied, share, copy } = usePersonaScriptClipboard(script);

  if (actionsPlacement === "header") return null;

  return (
    <div className="flex flex-col gap-2" data-testid="persona-script-actions-footer">
      <PersonaScriptBusyStatus isSuggesting={isSuggesting} isAiEditing={isAiEditing} />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <PersonaScriptShareButton linkCopied={linkCopied} onShare={() => void share()} />
            <PersonaScriptCopyButton copied={copied} onCopy={() => void copy()} />
            <PersonaScriptDownloadButton script={script} />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PersonaScriptAiEditButton isAiEditing={isAiEditing} onAiEdit={onAiEdit} />
            {showAddThreeHooks ? (
              <PersonaScriptAddThreeHooksButton
                isSuggesting={isSuggesting}
                onAddThreeHooks={onAddThreeHooks}
              />
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PersonaScriptVendorAction
            sentMeta={sentMeta}
            onSendToVendor={onSendToVendor}
            orderHref={orderHref}
            onOpenOrder={onOpenOrder}
          />
        </div>
      </div>
    </div>
  );
}
